/**
 * EditStudentDialog — admin/teacher edits non-identifying student fields.
 * Used from StudentSearch and the class-roster Students page.
 *
 * Roll number / class / section are intentionally NOT editable here — that
 * would corrupt attendance history. To move a student, use Admin → Import.
 */
import { useEffect, useState, useRef } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Loader2, Trash2, Upload, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

export default function EditStudentDialog({ open, onClose, student, onSaved, onDeleted }) {
  const { user } = useAuth();
  const canDelete = user?.role === "admin"; // writable admins (dev_admin maps to admin); super_admin is read-only
  const canEditPhoto = user?.role === "admin" || user?.role === "teacher";
  const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [loadingFull, setLoadingFull] = useState(false);
  const [hasPhoto, setHasPhoto] = useState(false);
  const [photoVer, setPhotoVer] = useState(0);
  const [photoBusy, setPhotoBusy] = useState(false);
  const photoInputRef = useRef(null);

  // When the dialog opens, fetch the FULL student record. The roster API only
  // returns a subset, so fields like dob and address would be empty otherwise.
  useEffect(() => {
    if (!open || !student?.id) return;
    // Pre-fill what we already know so the dialog isn't blank on open
    setForm({
      name: student.name || "",
      gender: student.gender || "",
      dob: student.dob || "",
      parent_name: student.parent_name || "",
      parent_phone: student.parent_phone || "",
      address: student.address || "",
      student_id: student.student_id || "",
    });
    setHasPhoto(!!student.has_photo);
    setPhotoVer(Date.now());
    let cancelled = false;
    setLoadingFull(true);
    (async () => {
      try {
        const { data } = await api.get(`/students/${student.id}`);
        if (cancelled) return;
        setForm({
          name: data.name || "",
          gender: data.gender || "",
          dob: data.dob || "",
          parent_name: data.parent_name || "",
          parent_phone: data.parent_phone || "",
          address: data.address || "",
          student_id: data.student_id || "",
        });
        setHasPhoto(!!data.has_photo);
      } catch (e) {
        if (!cancelled) toast.error(apiErr(e.response?.data?.detail) || "Could not load full record");
      } finally { if (!cancelled) setLoadingFull(false); }
    })();
    return () => { cancelled = true; };
  }, [open, student?.id]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onPickPhoto = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !student) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    if (f.size > 10 * 1024 * 1024) { toast.error("Image too large. Max 10 MB."); return; }
    setPhotoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/students/${student.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setHasPhoto(true);
      setPhotoVer(Date.now());
      onSaved && onSaved({ id: student.id, has_photo: true });
      toast.success("Student photo updated");
    } catch (e2) {
      toast.error(apiErr(e2.response?.data?.detail) || "Photo upload failed");
    } finally { setPhotoBusy(false); }
  };

  const onRemovePhoto = async () => {
    if (!student) return;
    setPhotoBusy(true);
    try {
      await api.delete(`/students/${student.id}/photo`);
      setHasPhoto(false);
      setPhotoVer(Date.now());
      onSaved && onSaved({ id: student.id, has_photo: false });
      toast.success("Photo removed");
    } catch (e2) {
      toast.error(apiErr(e2.response?.data?.detail) || "Could not remove photo");
    } finally { setPhotoBusy(false); }
  };

  const onSave = async () => {
    if (!student) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/students/${student.id}`, {
        name: form.name,
        student_id: form.student_id,
        gender: form.gender,
        dob: form.dob,
        parent_name: form.parent_name,
        parent_phone: form.parent_phone,
        address: form.address,
      });
      onSaved && onSaved({ id: student.id, ...data });
      toast.success("Student updated");
      onClose();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not update student");
    } finally { setSaving(false); }
  };

  const onDelete = async () => {
    if (!student) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/students/${student.id}`);
      toast.success(`${student.name} deleted`);
      onDeleted && onDeleted(student.id);
      onClose();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not delete student");
    } finally { setDeleting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="edit-student-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }} className="flex items-center gap-2">
            <span>Edit {student?.name || "student"}</span>
            {loadingFull && <Loader2 className="h-4 w-4 animate-spin text-slate-400" data-testid="edit-loading"/>}
          </DialogTitle>
          <DialogDescription>Update this student's profile details and class information, then save.</DialogDescription>
        </DialogHeader>

        {canEditPhoto && (
          <div className="flex items-center gap-4 rounded-2xl border border-slate-200 dark:border-slate-700 p-3 mb-1" data-testid="edit-photo-section">
            {hasPhoto ? (
              <img
                src={`${apiBase}/profile/photo/${student?.id}?v=${photoVer}`}
                alt={student?.name}
                className="h-16 w-16 rounded-2xl object-cover shrink-0"
                data-testid="edit-photo-preview"
              />
            ) : (
              <div className="h-16 w-16 rounded-2xl bg-sky-50 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300 flex items-center justify-center text-lg font-semibold shrink-0">
                {(student?.name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("")}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-slate-800 dark:text-slate-100">Student photo</div>
              <div className="text-xs text-slate-500 mb-2">Upload a clear front-facing snap. JPG/PNG/WebP, under 10 MB.</div>
              <input ref={photoInputRef} type="file" accept="image/*" className="hidden" onChange={onPickPhoto} data-testid="edit-photo-input" />
              <div className="flex items-center gap-2">
                <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => photoInputRef.current?.click()} disabled={photoBusy} data-testid="edit-photo-upload-btn">
                  {photoBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : hasPhoto ? <Upload className="h-4 w-4 mr-1.5" /> : <ImageIcon className="h-4 w-4 mr-1.5" />}
                  {hasPhoto ? "Change photo" : "Upload photo"}
                </Button>
                {hasPhoto && (
                  <Button type="button" variant="ghost" size="sm" className="rounded-full text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40" onClick={onRemovePhoto} disabled={photoBusy} data-testid="edit-photo-remove-btn">
                    <Trash2 className="h-4 w-4 mr-1.5" /> Remove
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-4 py-2">
          <Field label="Name" id="edit-name">
            <Input id="edit-name" value={form.name || ""} onChange={set("name")} data-testid="edit-name-input"/>
          </Field>
          <Field label="Student ID (9 digits)" id="edit-student-id">
            <Input id="edit-student-id" inputMode="numeric" maxLength={9} placeholder="e.g. 100000099"
              value={form.student_id || ""}
              onChange={(e) => setForm((f) => ({ ...f, student_id: e.target.value.replace(/\D/g, "").slice(0, 9) }))}
              data-testid="edit-student-id-input"/>
          </Field>
          <Field label="Gender" id="edit-gender">
            <Input id="edit-gender" value={form.gender || ""} onChange={set("gender")} placeholder="male / female / other" data-testid="edit-gender-input"/>
          </Field>
          <Field label="Date of birth" id="edit-dob">
            <DatePicker value={form.dob || ""} onChange={(v) => setForm((f) => ({ ...f, dob: v }))} data-testid="edit-dob-input"/>
          </Field>
          <Field label="Parent name" id="edit-parent-name">
            <Input id="edit-parent-name" value={form.parent_name || ""} onChange={set("parent_name")} data-testid="edit-parent-name-input"/>
          </Field>
          <Field label="Parent phone" id="edit-parent-phone" full>
            <Input id="edit-parent-phone" value={form.parent_phone || ""} onChange={set("parent_phone")} placeholder="e.g. 9876543210" data-testid="edit-parent-phone-input"/>
          </Field>
          <Field label="Address" id="edit-address" full>
            <Input id="edit-address" value={form.address || ""} onChange={set("address")} data-testid="edit-address-input"/>
          </Field>
        </div>
        <div className="text-xs text-slate-500 pt-1">
          Roll number, class and section can&apos;t be edited here — that would break attendance history. Use Admin → Import to move a student.
        </div>
        <DialogFooter>
          {canDelete && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" disabled={saving || deleting} className="mr-auto border-rose-300 text-rose-600 hover:bg-rose-50 hover:text-rose-700" data-testid="edit-delete-btn">
                  {deleting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />}
                  Delete student
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="delete-student-confirm">
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete {student?.name}?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This permanently removes the student and their attendance, marks and tutor-session history. This cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={onDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="delete-confirm-btn">Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          <Button variant="outline" onClick={onClose} disabled={saving || deleting} data-testid="edit-cancel-btn">Cancel</Button>
          <Button onClick={onSave} disabled={saving || deleting} className="bg-sky-500 hover:bg-sky-600" data-testid="edit-save-btn">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, id, children, full }) {
  return (
    <div className={full ? "sm:col-span-2 space-y-1.5" : "space-y-1.5"}>
      <Label htmlFor={id} className="text-xs uppercase tracking-[0.15em] text-slate-500">{label}</Label>
      {children}
    </div>
  );
}
